import ReactMarkdown from 'react-markdown'
import QuantityStepper from './QuantityStepper'
import type { PurchasableItem } from '@/types'

const ALLOWED_ELEMENTS = ['p', 'ul', 'ol', 'li', 'strong', 'em', 'a']

type Props = {
  item: PurchasableItem
  quantity: number
  onChange: (next: number) => void
  maxPerTier: number
  subtitle?: string
}

function formatDollars(cents: number) {
  return (cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

export default function TierCard({ item, quantity, onChange, maxPerTier, subtitle }: Props) {
  const perPurchase = Math.max(1, item.tickets_per_purchase)
  const remainingPackages =
    item.inventory_limit != null
      ? Math.max(0, Math.floor((item.inventory_limit - item.inventory_sold) / perPurchase))
      : null
  const soldOut = remainingPackages !== null && remainingPackages <= 0
  const buyerLimit = item.max_per_buyer ?? maxPerTier
  const cap = remainingPackages !== null ? Math.min(remainingPackages, maxPerTier, buyerLimit) : Math.min(maxPerTier, buyerLimit)
  const selected = quantity > 0
  const lowStock = remainingPackages !== null && remainingPackages > 0 && remainingPackages <= 5

  return (
    <div
      aria-disabled={soldOut}
      data-selected={selected || undefined}
      className="group rounded-xl border border-zinc-950/10 bg-background px-4 py-3.5 not-data-selected:hover:border-zinc-950/20 data-selected:border-primary/40 data-selected:bg-primary/[0.025] aria-disabled:opacity-50"
    >
      <div className="flex items-baseline justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="font-medium text-foreground">{item.name}</h3>
          {subtitle && (
            <p className="mt-0.5 text-xs uppercase tracking-wide text-muted-foreground">{subtitle}</p>
          )}
          <div className="mt-1 flex items-center gap-2.5">
            <p className="text-base font-semibold tabular-nums text-foreground">
              ${formatDollars(item.price_cents)}
            </p>
            {perPurchase > 1 && (
              <p className="text-xs text-muted-foreground">
                Includes {perPurchase} tickets
              </p>
            )}
            {remainingPackages !== null && !soldOut && (
              <p
                className={`text-xs tabular-nums ${
                  lowStock ? 'text-amber-700' : 'text-muted-foreground'
                }`}
              >
                {remainingPackages} left
              </p>
            )}
          </div>
        </div>
        <div className="shrink-0">
          {soldOut ? (
            <span className="inline-flex items-center rounded-full bg-zinc-950/5 px-2.5 py-1 text-xs font-medium text-muted-foreground">
              Sold out
            </span>
          ) : (
            <QuantityStepper value={quantity} onChange={onChange} max={cap} ariaName={item.name} />
          )}
        </div>
      </div>
      {item.description && (
        <div className="prose prose-sm mt-2 max-w-none text-sm text-muted-foreground prose-li:my-0 prose-ul:my-1 prose-ul:pl-4">
          <ReactMarkdown allowedElements={ALLOWED_ELEMENTS} unwrapDisallowed>
            {item.description}
          </ReactMarkdown>
        </div>
      )}
    </div>
  )
}
